
import { motion, useTransform, type MotionValue } from "framer-motion";
import { SNAP_FADE } from "../keyframe-data";
import type { KeyframeBand } from "../types";
import {
  LAYOUT,
  EDGE,
  DIM_OPACITY,
  FINDING_NODE,
  AFFECTED_NODE,
  polylinePath,
  getNode,
} from "../graph-layout";
import { NodeCard } from "../elements/node-card";
import { FindingChip, findingChipWidth } from "../elements/finding-chip";

const FINDING_LABEL = "No Retention Period";

interface Props {
  progress: MotionValue<number>;
  /** Beat 6 band. */
  band: KeyframeBand;
}

export function Beat6Outputs({ progress, band }: Props) {
  const FADE = SNAP_FADE;
  const fadeIn = useTransform(
    progress,
    [band.start, band.start + FADE],
    [0, 1],
  );

  const ddb = getNode(FINDING_NODE);
  const ddbSnap = getNode(AFFECTED_NODE);

  const litCopyEdge = LAYOUT.edges.find(
    (e) => e.from === FINDING_NODE && e.to === AFFECTED_NODE,
  );
  const litCopyPath = litCopyEdge ? polylinePath(litCopyEdge.points) : "";

  // Finding chip below DDB. PERSONAL DATA chips uniformly take the "above"
  // position for all PII nodes, so FINDING anchors below to keep the chip
  // families separable.
  const chipW = findingChipWidth(FINDING_LABEL);
  const chipX = ddb.cx - chipW / 2;
  const chipY = ddb.y + ddb.h + 14;

  // Scale-pop entrance on the finding chip — anchors at the chip's own center
  // so the box stays put while it punches in.
  const chipCenterX = chipX + chipW / 2;
  const chipCenterY = chipY + 10;
  const chipPop = useTransform(
    progress,
    [band.start, band.start + FADE * 0.55, band.start + FADE * 1.4],
    [0.85, 1.06, 1],
  );
  const chipTransform = useTransform(
    chipPop,
    (s) => `translate(${chipCenterX} ${chipCenterY}) scale(${s}) translate(${-chipCenterX} ${-chipCenterY})`,
  );

  return (
    <motion.g style={{ opacity: fadeIn }} data-beat="6" aria-hidden="true">
      {/* Lit copy edge: DDB → DDB Snapshot, full-alpha foreground */}
      <path
        d={litCopyPath}
        fill="none"
        stroke={EDGE.flowLit}
        strokeWidth={EDGE.flowLitWidth}
        strokeDasharray={EDGE.copyDash}
        strokeLinejoin="round"
        strokeLinecap="round"
        markerEnd="url(#arrow-flow-lit)"
        className="edge-march"
      />

      {/* DDB lit (focus stroke) */}
      <NodeCard id="dynamo" x={ddb.x} y={ddb.y} w={ddb.w} state="focus" />

      {/* DDB Snapshot affected (dashed warning, lower alpha) */}
      <NodeCard id="ddb-snap" x={ddbSnap.x} y={ddbSnap.y} w={ddbSnap.w} state="affected" />

      {/* Finding chip above DDB — pops in at beat 6 start */}
      <motion.g style={{ transform: chipTransform }}>
        <FindingChip x={chipX} y={chipY} label={FINDING_LABEL} />
      </motion.g>
    </motion.g>
  );
}

/** Cumulative dim multiplier applied to non-focused elements once beat 5's mid-point is crossed.
 * Pass beat 5's band so the helper works against pin-2-relative progress. */
export function useDimAtBeat5Plus(progress: MotionValue<number>, beat5Band: KeyframeBand) {
  const beat5Mid = beat5Band.start + (beat5Band.end - beat5Band.start) / 2;
  return useTransform(progress, [beat5Mid, beat5Band.end], [1, DIM_OPACITY]);
}
